import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CheckIcon from '@mui/icons-material/Check';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from 'hooks/useAuth';
import useThemeStore, { useMode } from 'stores/themeStore';
import { useTokens } from 'theme';
import useAuthStore, { selectFeature } from 'stores/authStore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const planFill = (t) => ({
  free:       { bg: t.raised,   color: t.inkMuted },
  starter:    { bg: t.infoBg,   color: t.link },
  pro:        { bg: t.accent,   color: t.onAccent },
  enterprise: { bg: t.ink,      color: t.surface },
});

function PlanTag({ plan }) {
  useTranslation();
  const t = useTokens();
  const { bg, color } = planFill(t)[plan] || { bg: t.raised, color: t.inkMuted };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: '7px',
        bgcolor: bg,
        color,
        borderRadius: 999,
        fontSize: '0.6rem',
        fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        lineHeight: 1.7,
        flexShrink: 0,
      }}
    >
      {plan}
    </Box>
  );
}

const initialsOf = (user) => (user?.name
  ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
  : user?.email?.[0]?.toUpperCase() || '?');

/**
 * The account block at the foot of the sidebar (as in Claude Desktop): who is
 * signed in and into which project; it opens the menu that used to live in the
 * topbar — project switch, theme, settings, sign-out.
 */
export default function AccountMenu() {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const navigate = useNavigate();
  const { user, activeMembership, memberships, switchTenant, logout } = useAuth();
  const accountEnabled = useAuthStore(selectFeature('account_console'));
  const mode = useMode();
  const toggleTheme = useThemeStore((s) => s.toggle);
  const [anchor, setAnchor] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const close = () => setAnchor(null);
  const tenant = activeMembership?.tenant;

  const handleSwitch = async (slug) => {
    close();
    if (slug === tenant?.slug) return;
    setSwitching(true);
    try { await switchTenant(slug); }
    finally { setSwitching(false); }
  };

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={translate('shell.accountProject')}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1,
          py: 0.875,
          borderRadius: '8px',
          textAlign: 'left',
          color: t.onChrome,
          '&:hover': { bgcolor: t.chromeAlt },
          '&.Mui-focusVisible': { outline: `2px solid ${t.focus}` },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: t.accent,
            color: t.onAccent,
            fontWeight: 800,
            fontSize: '0.78rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {switching ? <CircularProgress size={14} sx={{ color: t.onAccent }} /> : initialsOf(user)}
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 700, lineHeight: 1.3 }}>
            {user?.name || user?.email}
          </Typography>
          {tenant && (
            <Typography noWrap sx={{ fontSize: '0.72rem', color: t.onChromeMuted, lineHeight: 1.3 }}>
              {tenant.name}
            </Typography>
          )}
        </Box>
        <UnfoldMoreIcon sx={{ fontSize: 18, color: t.onChromeMuted, flexShrink: 0 }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { width: 264, mb: 1 } }}
      >
        <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
          <Typography noWrap sx={{ fontSize: '0.8rem', color: t.inkMuted }}>{user?.email}</Typography>
        </Box>

        {/* A switch only means something with somewhere to switch to. An array,
            not a Fragment: Menu children must be its direct items. */}
        {memberships.length > 1 && [
          <ListSubheader key="projects" sx={{ lineHeight: '28px', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.inkMuted, bgcolor: 'transparent' }}>{translate('shell.projects')}</ListSubheader>,
          ...memberships.map((m) => {
            const isActive = m.tenant.slug === tenant?.slug;
            return (
              <MenuItem key={m.tenant.slug} selected={isActive} onClick={() => handleSwitch(m.tenant.slug)} sx={{ gap: 1 }}>
                <Typography noWrap sx={{ flexGrow: 1, fontSize: '0.875rem', fontWeight: isActive ? 700 : 500 }}>
                  {m.tenant.name}
                </Typography>
                <PlanTag plan={m.tenant.plan} />
                <Box sx={{ width: 16, display: 'flex' }}>{isActive && <CheckIcon sx={{ fontSize: 16 }} />}</Box>
              </MenuItem>
            );
          }),
        ]}

        <Divider />
        <MenuItem onClick={() => { toggleTheme(); close(); }}>
          <ListItemIcon>
            {mode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
          </ListItemIcon>
          {mode === 'dark' ? translate('shell.lightTheme') : translate('shell.darkTheme')}
        </MenuItem>
        <MenuItem onClick={() => { close(); navigate('/console/settings'); }}>
          <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>{translate('shell.settings')}</MenuItem>
        {accountEnabled && <MenuItem onClick={() => { close(); navigate('/console/settings?tab=profile'); }}>
          <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>{translate('shell.myAccount')}</MenuItem>}
        <Divider />
        <MenuItem onClick={() => { close(); setLogoutOpen(true); }} sx={{ color: t.negative }}>
          <ListItemIcon sx={{ color: 'inherit' }}><LogoutIcon fontSize="small" /></ListItemIcon>{translate('shell.signOut')}</MenuItem>
      </Menu>

      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{translate('shell.signOutTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: t.inkMuted }}>
            {user?.email ? translate('shell.signOutEmail', { email: user.email }) : translate('shell.signOutBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setLogoutOpen(false)}>{translate('shell.cancel')}</Button>
          <Button
            variant="contained"
            startIcon={<LogoutIcon fontSize="small" />}
            onClick={() => { setLogoutOpen(false); logout(); }}
          >{translate('shell.signOut')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
